export interface PanelSize {
  w: number;
  h: number;
}

/** What a note's panel opens at the first time. */
export const FIRST_OPEN: PanelSize = { w: 500, h: 640 };
export const PANEL_MIN: PanelSize = { w: 380, h: 240 };
export const PANEL_MAX_H = 1600;

const KEY = 'pondering.notePanelSizes';

type Sizes = Record<string, PanelSize>;

function read(): Sizes {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? '{}') as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Sizes) : {};
  } catch {
    return {};
  }
}

const sound = (s: PanelSize | undefined): s is PanelSize => !!s && Number.isFinite(s.w) && Number.isFinite(s.h);

/** The size a note's panel was left at, kept in this browser alone: a view, so it is no write to
 *  the vault and does not follow the person to another device. */
export function panelSizeOf(path: string): PanelSize {
  const size = read()[path];
  return sound(size) ? size : FIRST_OPEN;
}

export function keepPanelSize(path: string, size: PanelSize): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...read(), [path]: { w: Math.round(size.w), h: Math.round(size.h) } }));
  } catch {
    // Storage may be off or full; the size is then forgotten with the panel.
  }
}

/** [size] held between the smallest a panel reads at and what the page allows; a page with no
 *  width yet puts no limit on it. */
export function fitPanelSize(size: PanelSize, page: PanelSize): PanelSize {
  return {
    w: Math.max(PANEL_MIN.w, Math.min(size.w, page.w > 0 ? page.w - 24 : Infinity)),
    h: Math.max(PANEL_MIN.h, Math.min(size.h, PANEL_MAX_H)),
  };
}
